/**
 * CohortMetricsHero — the Act 1 centerpiece.
 *
 * Five metric tiles that answer the questions an SVP asks in the
 * first three seconds of looking at a cohort:
 *
 *   1. Is this cohort structurally sound?   — CMD Integrity rings
 *   2. How much new audience does it unlock? — total unique reach
 *   3. How redundant is it?                  — avg overlap %
 *   4. How well does it match the brand?     — avg DNA match %
 *   5. What's the coverage gap?              — covered/missing dims
 *
 * Each tile carries: small uppercase label, large value (weight 900),
 * color-coded status word, one-line context.
 *
 * Visual posture: confident dashboard. Generous padding. Large
 * numbers. Color tells the brand the verdict at a glance — they
 * don't need to read every word.
 *
 * Sits between the fingerprint chips frame above and the creator
 * grid below. Eliminates dead space and becomes the dominant visual
 * element of Act 1.
 */
import React from 'react';
import { Check, X } from 'lucide-react';
import { formatReach } from '../../lib/format';

const TEAL        = '#00D4FF';
const PINK        = '#FF4D94';
const GREEN_OK    = '#22D3A1';
const AMBER_WARN  = '#F59E0B';
const RED_BAD     = '#EF4444';
const NEUTRAL_BG  = 'rgba(255,255,255,0.025)';
const NEUTRAL_BD  = 'rgba(255,255,255,0.07)';

// Color per qualitative status. Used for both metric value tinting
// and status-word color so the hero reads coherently at a glance.
function statusColor(status) {
  if (status === 'strong')   return GREEN_OK;
  if (status === 'moderate') return AMBER_WARN;
  if (status === 'weak')     return RED_BAD;
  if (status === 'pending')  return '#888';
  return TEAL;
}

export default function CohortMetricsHero({ hero, tickedRingKeys }) {
  if (!hero) return null;
  const integrity = hero.integrity || { passed: 0, total: 3, checks: [] };
  const ticked = tickedRingKeys || new Set();
  // Until Act 2 starts ticking the rings, render them in "pending"
  // state — the brand sees the count but not the per-check verdict
  // yet. This keeps the rings as a SUSPENSE element that Act 2's
  // sequence resolves visually live.
  const allTicked = integrity.checks.every(c => ticked.has(c.key));
  const integrityStatus = !allTicked
    ? 'pending'
    : integrity.passed === integrity.total ? 'strong'
    : integrity.passed >= integrity.total - 1 ? 'moderate'
    : 'weak';

  // Coverage tile — derive label from the breakdown
  const coverageCovered = (hero.coverage_breakdown || []).filter(r => r.status === 'covered').length;
  const coverageTotal   = (hero.coverage_breakdown || []).length;
  const coverageStatus =
    coverageCovered === coverageTotal ? 'strong'
    : coverageCovered >= coverageTotal / 2 ? 'moderate'
    : 'weak';
  const missingLabels = (hero.coverage_breakdown || [])
    .filter(r => r.status === 'missing')
    .map(r => friendlyCoverageLabel(r.label));

  return (
    <div
      data-testid="cohort-metrics-hero"
      className="grid grid-cols-5 gap-3 px-1 mb-4"
    >
      {/* Tile 1 — CMD INTEGRITY */}
      <Tile
        label="CMD Integrity"
        testid="hero-integrity"
      >
        <div className="flex items-center justify-between">
          <span
            className="text-[34px] font-black leading-none"
            style={{ color: integrityStatus === 'pending' ? '#666' : statusColor(integrityStatus) }}
          >
            {allTicked ? integrity.passed : '–'}<span className="text-[18px] text-gray-600">/{integrity.total}</span>
          </span>
          <div className="flex gap-1">
            {integrity.checks.map((c) => {
              const isTicked = ticked.has(c.key);
              return (
                <span
                  key={c.key}
                  title={`${c.label}: ${c.detail}`}
                  data-testid={`hero-integrity-ring-${c.key}`}
                  data-passed={c.passed ? 'true' : 'false'}
                  data-ticked={isTicked ? 'true' : 'false'}
                  className="h-4 w-4 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{
                    background: !isTicked
                      ? 'rgba(255,255,255,0.04)'
                      : c.passed ? `${GREEN_OK}22` : `${RED_BAD}15`,
                    border: `1.5px solid ${
                      !isTicked
                        ? 'rgba(255,255,255,0.18)'
                        : c.passed ? GREEN_OK : RED_BAD
                    }`,
                    boxShadow: isTicked ? `0 0 12px ${c.passed ? GREEN_OK : RED_BAD}66` : 'none',
                  }}
                >
                  {isTicked && (c.passed
                    ? <Check className="h-2.5 w-2.5" style={{ color: GREEN_OK }} strokeWidth={3} />
                    : <X     className="h-2.5 w-2.5" style={{ color: RED_BAD }}  strokeWidth={3} />)}
                </span>
              );
            })}
          </div>
        </div>
        <StatusWord
          status={integrityStatus}
          text={
            integrityStatus === 'pending' ? 'testing'
            : integrity.passed === integrity.total ? 'passed'
            : 'partial'
          }
        />
        <p className="text-[10px] text-gray-500 leading-snug truncate" data-testid="hero-integrity-detail">
          {integrityStatus === 'pending'
            ? 'running 3 structural checks…'
            : integrityChecksSummary(integrity)}
        </p>
      </Tile>

      {/* Tile 2 — UNIQUE REACH */}
      <Tile
        label="New audience unlocked"
        testid="hero-unique-reach"
      >
        <p className="text-[34px] font-black leading-none" style={{ color: TEAL }}>
          +{formatReach(hero.total_unique_reach || 0)}
        </p>
        <StatusWord status="strong" text="net new" />
        <p className="text-[10px] text-gray-500 leading-snug">
          across {hero.creator_count || 'this'} cohort{hero.creator_count === 1 ? '' : ''}
        </p>
      </Tile>

      {/* Tile 3 — AVG OVERLAP */}
      <Tile
        label="Audience overlap"
        testid="hero-avg-overlap"
      >
        <p
          className="text-[34px] font-black leading-none"
          style={{ color: statusColor(hero.overlap_status) }}
        >
          {hero.avg_overlap_pct ?? 0}%
        </p>
        <StatusWord
          status={hero.overlap_status}
          text={hero.overlap_status === 'strong' ? 'low redundancy'
              : hero.overlap_status === 'moderate' ? 'some redundancy'
              : 'high redundancy'}
        />
        <p className="text-[10px] text-gray-500 leading-snug">
          {hero.overlap_status === 'strong'
            ? 'creators reach distinct audiences'
            : hero.overlap_status === 'moderate'
              ? 'modest audience duplication'
              : 'tighten cohort to reduce duplication'}
        </p>
      </Tile>

      {/* Tile 4 — AVG DNA MATCH */}
      <Tile
        label="Brand fit"
        testid="hero-avg-match"
      >
        <p
          className="text-[34px] font-black leading-none"
          style={{ color: statusColor(hero.match_status) }}
        >
          {hero.avg_match_pct ?? 0}%
        </p>
        <StatusWord
          status={hero.match_status}
          text={hero.match_status === 'strong' ? 'strong match'
              : hero.match_status === 'moderate' ? 'moderate match'
              : 'weak match'}
        />
        <p className="text-[10px] text-gray-500 leading-snug">
          average alignment with your fingerprint
        </p>
      </Tile>

      {/* Tile 5 — COVERAGE GAP */}
      <Tile
        label="Data room coverage"
        testid="hero-coverage"
      >
        <div className="flex items-baseline gap-1.5">
          <p
            className="text-[34px] font-black leading-none"
            style={{ color: statusColor(coverageStatus) }}
          >
            {coverageCovered}<span className="text-[18px] text-gray-600">/{coverageTotal}</span>
          </p>
          <div className="flex gap-1 ml-auto">
            {(hero.coverage_breakdown || []).map((row) => (
              <span
                key={row.source}
                title={`${row.label}: ${row.status}`}
                data-testid={`hero-coverage-dot-${row.source}`}
                data-status={row.status}
                className="h-2 w-2 rounded-full"
                style={{
                  background: row.status === 'covered' ? GREEN_OK : AMBER_WARN,
                  boxShadow:  row.status === 'covered' ? `0 0 6px ${GREEN_OK}88` : 'none',
                }}
              />
            ))}
          </div>
        </div>
        <StatusWord
          status={coverageStatus}
          text={coverageStatus === 'strong' ? 'fully covered'
              : coverageStatus === 'moderate' ? 'partial coverage'
              : 'narrow coverage'}
        />
        <p className="text-[10px] text-gray-500 leading-snug truncate" title={missingLabels.join(' · ')}>
          {missingLabels.length === 0
            ? 'all dimensions represented'
            : `gap: ${missingLabels.join(' · ')}`}
        </p>
      </Tile>
    </div>
  );
}


// ─── Tile primitive ─────────────────────────────────────────────
function Tile({ label, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl p-3 space-y-1.5"
      style={{
        background: NEUTRAL_BG,
        border:     `1px solid ${NEUTRAL_BD}`,
      }}
    >
      <p className="text-[9px] uppercase tracking-[0.18em] font-black text-gray-500">
        {label}
      </p>
      {children}
    </div>
  );
}


function StatusWord({ status, text }) {
  return (
    <p
      className="text-[10px] uppercase tracking-[0.15em] font-black"
      style={{ color: statusColor(status) }}
      data-status={status}
    >
      {text}
    </p>
  );
}


// One-liner narrating the 3 integrity checks for the tile sub-line.
// Reads the failing check first if there is one ("Expansion lift
// weak"), otherwise summarizes the passing posture.
function integrityChecksSummary(integrity) {
  const failed = integrity.checks?.find(c => !c.passed);
  if (failed) {
    return `${failed.label.toLowerCase()} — ${failed.detail.toLowerCase()}`;
  }
  return 'all 3 structural checks passed';
}


function friendlyCoverageLabel(rawLabel) {
  if (!rawLabel) return '';
  const lower = rawLabel.toLowerCase();
  const cleaned = lower
    .replace(/^top\s+/, '')
    .replace(/^best[-\s]performing\s+/, '');
  return cleaned
    .replace(/\bskus by past performance\b/gi, 'SKUs')
    .replace(/\bskus\b/g, 'SKUs')
    .replace(/\bcrm\b/g, 'CRM');
}


// Ensure unused color tokens don't trip the linter when only a subset
// of the palette is referenced (PINK is reserved for future Act 2 use).
// eslint-disable-next-line no-unused-vars
const _palette = { PINK };
